import { Box } from "@mui/material";
import { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { profileState } from "../../app/reducers/profile-slice";
import {
    clearSelectedClientIdsAction,
    clearSelectedScheduleAction,
    clearSelectedStatusAction,
    scheduleState,
    setSelectedClientIdsAction,
    setSelectedStatusAction,
} from "../../app/reducers/schedule-slice";
import theme from "../../style/theme";
import { Schedule } from "../../types/schedule/schedule";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { scrollIntoTopByElementId } from "../../utils/document";
import { SearchDeliveryAutocomplete } from "../autocomplete/SearchDeliveryAutocomplete";
import Button from "../button/Button";
import { FilterChip } from "../chip/FilterChip";
import { AlertPanel } from "../alert-panel/AlertPanel";
import FilterLineIcon from "../icons/FilterLineIcon";
import LeftFoldIcon from "../icons/LeftFoldIcon/LeftFoldIcon";
import RightFoldIcon from "../icons/RightFoldIcon/RightFoldIcon";
import { ClientFilterMultiSelect2 } from "../selector/ClientFilterMultiSelect2";
import { StatusFilterMultiSelect } from "../selector/StatusFilterMultiSelect";
import { DashboardScheduleLine } from "../wrapper/DashboardScheduleLine";
import { EmptyScheduleCollapse } from "./EmptyScheduleCollapse";
import { EmptyScheduleExpand } from "./EmptyScheduleExpand";
import CircularProgress from "@mui/material/CircularProgress";

interface Props {
    selectedStatus: string[];
    selectedClientIds: string[];
}

const DashboardInfo: React.FC<Props> = (
    {
        selectedStatus,
        selectedClientIds,
    }
) => {
    const [open, setOpen] = useState(false);
    const initLayoutLeft = "calc(100vw - 169px)";

    const [layoutLeft, setLayoutLeft] = useState(initLayoutLeft);
    const [searchScheduleId, setSearchScheduleId] = useState("");
    const { selectedSchedule, schedulePageAll, isDashboardInit } = useAppSelector(scheduleState);
    const { profile } = useAppSelector(profileState);
    const [scheduleClientMap, setScheduleClientMap] = useState<{
        [key: string]: string;
    }>({});
    const dispatch = useAppDispatch();

    const scheduleStatusMap: {
        [key: string]: string;
    } = {
        WORK: "진행 중",
        COMPLETED: "모두 완료",
        WAIT: "시작 전",
    };

    const nodeRef = useRef(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (profile) {
            const map: {
                [key: string]: string;
            } = {};

            const allClients = [
                ...profile.clients.filter(client => client.status === "ACTIVE"),
                { id: profile.company.id, name: profile.company.name, status: "ACTIVE" }
            ];

            const sortedClients = allClients.sort((a, b) => a.name.localeCompare(b.name, 'ko'));

            sortedClients.forEach((client) => {
                map[client.id] = client.name;
            });
            setScheduleClientMap(map);
        }
    }, [profile]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, []);

    const handleOpenButton = () => {
        setOpen(!open);
        if (open) {
            setLayoutLeft(initLayoutLeft);
        } else {
            setLayoutLeft("40vw");
        }
    };

    const onClickStatusFilterMultiSelectItem = (value: string) => {
        dispatch(setSelectedStatusAction({ selectedStatus: value }));
    };

    const onClickClientFilterMultiSelectItem = (value: string) => {
        dispatch(setSelectedClientIdsAction({ selectedClientIds: value }));
    };

    const getScheduleId = (searchScheduleId?: string) => {
        setSearchScheduleId(searchScheduleId || "");
    };

    const displaySchedules = Boolean(searchScheduleId)
        ? schedulePageAll.contents.filter(
            (schedule) => schedule.id === searchScheduleId
        )
        : schedulePageAll.contents;

    const displaySchedulesSorted = Array.from(displaySchedules).sort((a, b) => {
        if (a.alias > b.alias) {
            return 1;
        } else if (a.alias < b.alias) {
            return -1;
        }
        return 0;
    });

    const getDashboardScheduleLineId = (id: string) => {
        return `display-schedule-line-${id}`;
    };

    useDidMountEffect(() => {
        if (selectedSchedule) {
            scrollIntoTopByElementId(getDashboardScheduleLineId(selectedSchedule.id));
        }
    }, [selectedSchedule]);

    const handleFilterClear = () => {
        dispatch(clearSelectedStatusAction());
        dispatch(clearSelectedScheduleAction());
        dispatch(clearSelectedClientIdsAction());
    }

    const filterSchedule = (schedules: Schedule[]) => {
        let filteredSchedules = schedules;
        if (selectedStatus.length > 0) {
            filteredSchedules = schedules.filter((displaySchedule) =>
                selectedStatus.includes(displaySchedule.status)
            );
        }
        if (selectedClientIds.length > 0) {
            filteredSchedules = filteredSchedules.filter((displaySchedule) =>
                selectedClientIds.some((clientId) =>
                    displaySchedule.clientIds.includes(clientId)
                )
            );
        }
        return filteredSchedules;
    };

    const [isFilterActive, setIsFilterActive] = useState(false);

    const updateHeight = () => {
        setIsFilterActive(selectedStatus.length > 0 || selectedClientIds.length > 0);
    };

    useEffect(() => {
        updateHeight();
    }, [selectedStatus, selectedClientIds]);

    return (
        <Box
            style={{
                height: "100%",
            }}
        >
            <Box
                style={{
                    position: "fixed",
                    left: layoutLeft || initLayoutLeft,
                    height: "100%",
                    width: "60%",
                    backgroundColor: "transparent",
                    display: "flex",
                    flexDirection: "row",
                    boxShadow: "-7px 0px 20px rgba(0, 0, 0, 0.06)",
                    flex: 1,
                }}
            >
                <Box
                    style={{
                        height: "100%",
                        width: "100%",
                        backgroundColor: `${theme.palette.primitives.gray50}`,
                        borderTopLeftRadius: "6px",
                        flex: 1,
                    }}
                >
                    <Box
                        style={{
                            position: "relative",
                            height: "66px",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            flexDirection: "row",
                            backgroundColor: "#FFFFFF",
                            borderBottom: `2px solid ${theme.palette.primitives.gray100}`,
                            gap: "12px",
                        }}
                    >
                        <DashboardTableToggleButton
                            size="square"
                            onClick={handleOpenButton}
                            startIcon={open ? <RightFoldIcon /> : <LeftFoldIcon />}
                        />
                        {profile && (
                            <AlertPanel
                                selectedStatus={selectedStatus}
                                selectedClientIds={selectedClientIds}
                            />
                        )}
                        <DashboardInfoTitle>오늘 전체 현황</DashboardInfoTitle>
                        <SearchDeliveryAutocomplete
                            getScheduleId={getScheduleId}
                            inputRef={searchInputRef}
                        />
                        <div
                            style={{
                                display: "flex",
                                gap: "12px",
                                boxSizing: "border-box",
                                padding: "0 24px 0 12px",
                                minWidth: "max-content",
                            }}
                        >
                            <StatusFilterMultiSelect
                                values={selectedStatus}
                                candidateMap={scheduleStatusMap}
                                onClickFilterMultiSelectItem={
                                    onClickStatusFilterMultiSelectItem
                                }
                            />
                            <ClientFilterMultiSelect2
                                values={selectedClientIds}
                                candidateMap={scheduleClientMap}
                                onClickFilterMultiSelectItem={
                                    onClickClientFilterMultiSelectItem
                                }
                            />
                        </div>
                    </Box>
                    {(!isDashboardInit) && (
                        <DashboardLoadingContainer
                            isOpen={open}>
                            <CircularProgress
                                sx={{
                                    color: theme.palette.primitives.yellow500,
                                    animation: 'spin 1.2s linear infinite',
                                    '@keyframes spin': {
                                        '0%': {
                                            opacity: 1,
                                        },
                                        '50%': {
                                            opacity: 0.5,
                                        },
                                        '100%': {
                                            opacity: 1,
                                        }
                                    }
                                }}
                                size={20}
                            />
                            {open && `배송 정보를 불러오는 중입니다`}
                        </DashboardLoadingContainer>
                    )}
                    {(selectedStatus.length > 0 || selectedClientIds.length > 0) && (
                        <DashboardFilterContainer>
                            <LeftSelectedItemIconWrapper>
                                <FilterLineIcon />
                            </LeftSelectedItemIconWrapper>
                            <DashboardFilterTitle>선택중 필터</DashboardFilterTitle>
                            <FilterClearChip
                                onClick={handleFilterClear}>
                                초기화
                            </FilterClearChip>
                            <RightDashboardTitleSeparator />
                            {selectedStatus.map((status) => (
                                <FilterChip
                                    text={scheduleStatusMap[status]}
                                    key={status}
                                    onClickFilterMultiSelectItem={() => {
                                        onClickStatusFilterMultiSelectItem(status);
                                    }}
                                />
                            ))}
                            {selectedClientIds.map((clientId) => (
                                <FilterChip
                                    text={scheduleClientMap[clientId]}
                                    key={clientId}
                                    onClickFilterMultiSelectItem={() => {
                                        onClickClientFilterMultiSelectItem(clientId);
                                    }}
                                />
                            ))}
                        </DashboardFilterContainer>
                    )}
                    {displaySchedulesSorted.length > 0 ? (
                        <Box
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                overflow: "scroll",
                                // flex: 1,
                            }}
                        >
                            <Box style={{
                                height: `calc(100vh - ${isFilterActive ? 172 : 127}px)`,
                                width: '100%',
                                whiteSpace: 'nowrap',
                            }}>
                                {filterSchedule(displaySchedulesSorted).map(
                                    (displaySchedule, index) => (
                                        <DashboardScheduleLineIdContainer
                                            id={getDashboardScheduleLineId(displaySchedule.id)}
                                            key={displaySchedule.id}
                                        >
                                            <DashboardScheduleLine
                                                key={displaySchedule.id}
                                                dashboardScheduleLineType="Dashboard"
                                                displaySchedule={displaySchedule}
                                            />
                                        </DashboardScheduleLineIdContainer>
                                    )
                                )}
                            </Box>
                        </Box>
                    ) : open ? (
                        <EmptyScheduleExpandContainer>
                            <EmptyScheduleExpand />
                        </EmptyScheduleExpandContainer>
                    ) : (
                        <EmptyScheduleCollapseContainer>
                            <EmptyScheduleCollapse />
                        </EmptyScheduleCollapseContainer>
                    )}
                </Box>
            </Box>
        </Box>
    );
};

export default DashboardInfo;

const DashboardScheduleLineIdContainer = styled.div``;

const DashboardTableToggleButton = styled(Button)`
  position: absolute;
  top: 12px;
  left: -24px;
  margin-top: 58px;
  background-color: #333333;
  min-width: 24px;
  width: 24px;
  height: 72px;
  border-radius: 4px 0px 0px 4px;
  padding: 0;

  &:hover,
  &:active {
    background-color: ${theme.palette.primitives.gray700};
  }
`;

const FilterClearChip = styled.div`
  height: 24px;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 4px;
  padding: 0 8px;
  flex-shrink: 0;
  background-color: ${theme.palette.primitives.gray50};
  border: 1px solid ${theme.palette.primitives.gray200};
  margin: 0 4px;
  cursor: pointer;
  transition: all 0.2s ease-in-out;

  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  color: ${theme.palette.primitives.gray500};

  &:hover {
    background-color: ${theme.palette.primitives.gray100};
    border-color: ${theme.palette.primitives.gray300};
  }
`;

const DashboardInfoTitle = styled.div`
  width: 169px;
  min-width: 169px;
  padding-left: 24px;
  text-align: left;
  font-weight: 700;
  font-size: 18px;
  line-height: 22px;
  color: #000000;
  box-sizing: border-box;
  margin-right: 12px;
`;

const DashboardLoadingContainer = styled.div`
  width: 100%;
  height: 47px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: ${(props: { isOpen: boolean }) => (props.isOpen ? 'center' : 'flex-start')};
  background-color: ${theme.palette.primitives.yellow50};
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
  color: ${theme.palette.primitives.yellow700};
  padding-left: ${(props: { isOpen: boolean }) => (props.isOpen ? '0' : '70px')};
  border-bottom: 1px solid ${theme.palette.primitives.yellow100};
  box-shadow: inset 0 -1px 0 ${theme.palette.primitives.yellow100};
  animation: pulse 2s infinite ease-in-out;

  @keyframes pulse {
    0% {
      background-color: ${theme.palette.primitives.yellow50};
    }
    50% {
      background-color: ${theme.palette.primitives.yellow100};
    }
    100% {
      background-color: ${theme.palette.primitives.yellow50};
    }
  }
`;

const DashboardFilterContainer = styled.div`
  width: 100%;
  height: 47px;
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${theme.palette.primitives.yellow50};
`;

const DashboardFilterTitle = styled.div`
  height: 47px;
  display: flex;
  color: #71490d;
  flex-direction: row;
  font-weight: 500;
  align-items: center;
  background-color: ${theme.palette.primitives.yellow50};
  font-size: 13px;
`;

const LeftSelectedItemIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  box-sizing: border-box;
  padding: 0 0 0 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 4px;
`;

const RightDashboardTitleSeparator = styled.div`
  height: 11px;
  width: 1px;
  background-color: ${theme.palette.primitives.gray200};
  margin-left: 11.5px;
  margin-right: 11.5px;
`;

const EmptyScheduleExpandContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 77%;
`;

const EmptyScheduleCollapseContainer = styled.div`
  display: flex;
  // justify-content: center;
  align-items: center;
  width: 72%;
  height: 77%;
`;
